<template>
  <div v-if="isLoading">
    <v-skeleton-loader type="heading@2" class="breadcrumbs-skeleton-loader" />
  </div>
  <div v-else class="breadcrumbs-wrapper">
    <div class="breadcrumb-level">
      {{ projectName }}
      <span class="breadcrumb-separator">/</span>
    </div>
    <BreadCrumbsDropdown :menu-pages="menuPages" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useInsightsProject } from '@/stores/insights/insightsProject';
import BreadCrumbsDropdown from '@/components/shared/BreadCrumbsDropdown.vue';
import { getReportingQueryParam } from '@/api/helpers/insights/insights';

// Router
const route = useRoute();

defineProps({
  isLoading: { type: Boolean, default: false },
});

// Stores
const insightsProjectStore = useInsightsProject();
const { projectName } = storeToRefs(insightsProjectStore);

// Computed
const groupOnePages = computed(() => ([
  {
    sectionName: 'Reports',
    links: [
      {
        to: 'language-snapshot',
        name: 'Language Snapshot',
        query: getReportingQueryParam(route, 'language-snapshot'),
      },
      {
        to: 'content-snapshot',
        name: 'Content Snapshot',
        query: getReportingQueryParam(route, 'content-snapshot'),
      },
    ],
  },
]));

const groupTwoPages = computed(() => ([
  {
    sectionName: 'Reports',
    links: [
      {
        to: 'compliance-report',
        name: 'Compliance Report',
        query: getReportingQueryParam(route, 'compliance-report'),
      },
      {
        to: 'employer-brand-report',
        name: 'Employer Brand Report',
        query: getReportingQueryParam(route, 'employer-brand-report'),
      },
    ],
  },
]));

const groupThreePages = computed(() => ([
  {
    sectionName: 'Reports',
    links: [
      {
        to: 'job-performance-report',
        name: 'Job Performance Report',
        query: getReportingQueryParam(route, 'job-performance-report'),
      },
      {
        to: 'recruiting-performance',
        name: 'Recruiting Performance',
        query: getReportingQueryParam(route, 'recruiting-performance'),
      },
    ],
  },
]));

const menuPages = computed(() => ({
  'language-snapshot': groupOnePages.value,
  'content-snapshot': groupOnePages.value,
  'employer-brand-report': groupTwoPages.value,
  'compliance-report': groupTwoPages.value,
  'job-performance-report': groupThreePages.value,
  'recruiting-performance': groupThreePages.value,
}[route.name]));
</script>

<style scoped lang="less">
.breadcrumbs-skeleton-loader {
  display: flex;
  flex-wrap: unset;
  background: transparent;
  width: 300px;
}

.breadcrumbs-wrapper {
  display: flex;
  align-items: center;
}

.breadcrumb-level {
  font-size: 16px;
  font-weight: 500;
  color: @text-dark;
  line-height: 20px;
}

.breadcrumb-separator {
  font-size: 16px;
  font-weight: 500;
  color: @grey-500;
  padding-left: 4px;
  padding-right: 8px;
}
</style>
